import React, { useEffect, useMemo, useState } from 'react';
import {
  useQuery,
  useMutation,
  DocumentNode,
  OperationVariables,
} from '@apollo/client';
import { message } from 'antd';
import ListManager from '../../common/ListManager';
import Loading from '../../Loading';
import BorderedSection from '../BorderedSection';
import FilterHeader from '../FilterHeader';
import QueryErrorBoundary from '../../error-handling';
import { Button } from '../../Button';
import { set, get } from 'lodash';
import SaveInstructions from '../SaveInstructions';

type GenericListManagerProps<T> = {
  fetchAllQuery: {
    query: DocumentNode;
    identifier: string;
    variables?: OperationVariables;
    filterFn?: (item: any) => boolean;
  };
  fetchSelectedQuery: {
    query: DocumentNode;
    identifier: string;
    variables?: OperationVariables;
    variableName?: string;
    filterFn?: (item: any) => boolean;
  };
  updateMutation: {
    mutation: DocumentNode;
    variableName?: string;
    formatFunction?: any;
    variableStructure?: any;
  };
  refetchQueries?: Array<{
    query: DocumentNode;
    variables?: OperationVariables;
  }>;
  itemKey: keyof T;
  itemLabel: string;
  addedTitle: string;
  availableTitle: string;
  extendOptions?: React.ReactNode;
  isLocation?: boolean;
  extendItemOptions?: (item: any) => React.ReactNode;
  beforeUpdate?: (items: T[]) => Promise<boolean>;
};

function GenericListManager<
  T extends Record<string, any>,
  TContext extends { checkedItems: string[] } = { checkedItems: string[] },
>({
  fetchAllQuery,
  fetchSelectedQuery,
  refetchQueries,
  updateMutation,
  itemKey,
  itemLabel,
  addedTitle,
  availableTitle,
  extendOptions,
  isLocation,
  extendItemOptions,
  beforeUpdate,
}: Readonly<GenericListManagerProps<T>>) {
  const [addedItems, setAddedItems] = useState<T[]>([]);
  const [availableItems, setAvailableItems] = useState<T[]>([]);
  const [filteredItems, setFilteredItems] = useState<T[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [originalSelectedItems, setOriginalSelectedItems] = useState<T[]>([]);

  const filterContext = useMemo<TContext>(() => {
    return {
      checkedItems: addedItems.map((item) => item[itemKey] as string),
    } as TContext;
  }, [addedItems]);

  const defaultGetFilterFn = (context: { checkedItems: string[] }) => {
    return (item: T) => !context.checkedItems.includes(item.uuid);
  };

  const allfilterFn = useMemo(() => {
    return (item: T) => {
      const userFilterFn = fetchAllQuery?.filterFn || (() => true);
      return defaultGetFilterFn(filterContext)(item) && userFilterFn(item);
    };
  }, [filterContext, fetchAllQuery?.filterFn]);

  const {
    data: allItems,
    loading: allLoading,
    error: fetchAllError,
  } = useQuery(fetchAllQuery?.query, {
    variables: fetchAllQuery?.variables ?? {},
    skip: !fetchAllQuery?.query,
  });

  const {
    data: selectedItems,
    loading: selectedLoading,
    error: fetchSelectedError,
  } = useQuery(fetchSelectedQuery?.query, {
    variables: fetchSelectedQuery?.variables ?? {},
    skip: !fetchSelectedQuery?.query,
  });

  const [updateSelectedItems, { loading: updateLoading }] = useMutation(
    updateMutation?.mutation,
    {
      refetchQueries: refetchQueries || [],
    },
  );

  const loadingStates = allLoading || selectedLoading || updateLoading;

  const orderedAvailableItems = useMemo(
    () =>
      [...filteredItems].sort((a, b) =>
        String(a[itemLabel]).localeCompare(String(b[itemLabel])),
      ),
    [filteredItems, itemLabel],
  );

  const orderedAddedItems = useMemo(
    () =>
      [...addedItems].sort((a, b) =>
        String(a[itemLabel]).localeCompare(String(b[itemLabel])),
      ),
    [addedItems, itemLabel],
  );

  const onSaveChanges = async () => {
    try {
      if (beforeUpdate) {
        const addedItemIds = new Set(addedItems.map((item) => item[itemKey]));
        const itemsBeingRemoved = originalSelectedItems.filter(
          (item) => !addedItemIds.has(item[itemKey]),
        );

        const shouldProceed = await beforeUpdate(itemsBeingRemoved);
        if (!shouldProceed) {
          return;
        }
      }

      const payload = updateMutation?.variableStructure || {};

      set(
        payload,
        updateMutation.variableName ?? 'data',
        addedItems.map((item) =>
          updateMutation.formatFunction
            ? updateMutation.formatFunction(item[itemKey])
            : item[itemKey],
        ),
      );

      await updateSelectedItems({ variables: payload });
      message.success('Changes Saved');
    } catch (e) {
      const selected = originalSelectedItems;
      setAddedItems(selected);

      // Reapply the original filter logic when restoring state after mutation failure
      if (allItems) {
        const allFetchedItems = allItems[fetchAllQuery?.identifier] || [];
        const selectedIds = new Set(selected.map((item: T) => item[itemKey]));

        // Manually apply the same filtering logic as in the useEffect
        const filteredItems = allFetchedItems.filter((item: T) => {
          const userFilterFn = fetchAllQuery?.filterFn || (() => true);
          const notSelected = !selectedIds.has(item[itemKey]);
          const userFilterResult = userFilterFn(item);
          return notSelected && userFilterResult;
        });

        setAvailableItems(filteredItems);
        setFilteredItems(filteredItems);
      }

      message.error(e instanceof Error ? e.message : 'Something went wrong');
    }
  };

  const onAddItem = (item: T) => {
    setAddedItems((prev) => [...prev, item]);
    setAvailableItems((prev) =>
      prev.filter((i) => i[itemKey] !== item[itemKey]),
    );
    setFilteredItems((prev) =>
      prev.filter((i) => i[itemKey] !== item[itemKey]),
    );
  };

  const onRemoveItem = (item: T) => {
    setAddedItems((prev) => prev.filter((i) => i[itemKey] !== item[itemKey]));
    setAvailableItems((prev) => [...prev, item]);
    if (!searchTerm) setFilteredItems((prev) => [...prev, item]);
  };

  const filterAvailableItems = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    setFilteredItems(
      availableItems.filter((item) =>
        String(item[itemLabel]).toLowerCase().includes(value),
      ),
    );
  };

  let content;

  if (allLoading || selectedLoading || updateLoading) {
    content = <Loading />;
  } else {
    content = (
      <ListManager<any>
        addedItems={orderedAddedItems}
        availableItems={orderedAvailableItems}
        addedTitle={addedTitle}
        availableTitle={availableTitle}
        onAddItem={onAddItem}
        onRemoveItem={onRemoveItem}
        getItemLabel={(item) => String(item[itemLabel])}
        extendItemOptions={extendItemOptions}
        infoContent={<SaveInstructions isLocation={isLocation} />}
      />
    );
  }

  useEffect(() => {
    if (allItems) {
      const allFetchedItems = allItems[fetchAllQuery?.identifier] || [];

      const filteredItems = allFetchedItems.filter(allfilterFn);

      setAvailableItems(filteredItems);
      setFilteredItems(filteredItems);
    }
  }, [allItems, allfilterFn, fetchAllQuery?.identifier]);

  useEffect(() => {
    if (!selectedItems || !fetchSelectedQuery?.identifier) return;

    const selected = selectedItems[fetchSelectedQuery.identifier] ?? [];

    const filteredSelected = fetchSelectedQuery?.filterFn
      ? selected.filter(fetchSelectedQuery.filterFn)
      : selected;

    const modifiedSelected = fetchSelectedQuery?.variableName
      ? get(filteredSelected, fetchSelectedQuery.variableName, filteredSelected)
      : filteredSelected;

    if (!Array.isArray(modifiedSelected)) {
      return;
    }

    const selectedIds = new Set(
      modifiedSelected.map((item: T) => item[itemKey]),
    );

    setAddedItems(modifiedSelected);
    setOriginalSelectedItems(modifiedSelected);
    setAvailableItems((prev) =>
      prev.filter((item) => !selectedIds.has(item[itemKey])),
    );
    setFilteredItems((prev) =>
      prev.filter((item) => !selectedIds.has(item[itemKey])),
    );
  }, [selectedItems, fetchSelectedQuery, itemKey]);

  return (
    <>
      <QueryErrorBoundary error={[fetchAllError, fetchSelectedError]}>
        <BorderedSection
          title=""
          noPadding
          headingClassName="h-20"
          contentClassName="py-4"
          extendOptions={
            <div className="flex items-center gap-6">
              <FilterHeader
                searchTerm={searchTerm}
                onSearch={filterAvailableItems}
              />
              {extendOptions}
            </div>
          }
        >
          {content}
        </BorderedSection>
      </QueryErrorBoundary>
      <div className="flex items-center justify-center mt-4">
        <Button
          onClick={onSaveChanges}
          type="primary"
          className="text-sm mt-4 min-w-[180px]"
          disabled={loadingStates}
        >
          Save
        </Button>
      </div>
    </>
  );
}

export default GenericListManager;
